/**
 * Interface defining the structure for file type validation.
 * It includes arrays of valid file extensions and MIME types.
 */
export interface FileTypeValidation {
  extensions: string[] // Array of allowed file extensions
  mimeTypes: string[] // Array of allowed MIME types
}

/**
 * Validates if a given file matches the specified file type criteria.
 *
 * This function checks both the file extension and MIME type against
 * the provided validation rules. The validation passes if either the
 * file extension or the MIME type matches the criteria.
 *
 * @param {File} file - The file object to be validated
 * @param {FileTypeValidation} validTypes - Object containing arrays of valid extensions and MIME types
 * @returns {boolean} - Returns true if the file is valid, false otherwise
 */
export const isValidFileType = (file: File, validTypes: FileTypeValidation): boolean => {
  // Return false if no file is provided
  if (!file) return false

  // Convert file name and type to lowercase for case-insensitive comparison
  const fileName = file.name.toLowerCase()
  const fileType = file.type.toLowerCase()

  // Check if the file's MIME type matches any of the valid MIME types
  const isValidMimeType = validTypes.mimeTypes.some((type) => fileType === type.toLowerCase())

  // Check if the file's extension matches any of the valid extensions
  const isValidExtension = validTypes.extensions.some((ext) => fileName.endsWith(ext.toLowerCase()))

  // Return true if either the MIME type or extension is valid
  return isValidMimeType || isValidExtension
}
